import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatPrice, type Currency } from '@wishare/shared';
import { api } from '../lib/api.js';
import { pluralize } from '../lib/format.js';
import { Avatar, Badge, Button, EmptyState, Notice } from '../components/ui.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { IconEyeOff, IconGiving } from '../components/icons.js';

/**
 * «Я дарю» — что я обещал и кому.
 *
 * Экран существует только у дарителя. Получатель подарка не видит его никогда
 * и никак: данные для него просто не собираются на сервере.
 */

export function GiftsPage(): JSX.Element {
  const queryClient = useQueryClient();

  const { data: gifts, isLoading } = useQuery({
    queryKey: ['gifts'],
    queryFn: api.reservations.mine,
  });

  const markPurchased = useMutation({
    mutationFn: ({ itemId, purchased }: { itemId: string; purchased: boolean }) =>
      api.reservations.update(itemId, { status: purchased ? 'purchased' : 'reserved' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['gifts'] }),
  });

  const cancel = useMutation({
    mutationFn: (itemId: string) => api.reservations.remove(itemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gifts'] });
      void queryClient.invalidateQueries({ queryKey: ['list'] });
    },
  });

  /* Шапка данных не ждёт — пишем один раз и показываем в обоих состояниях. */
  const шапка = (
    <div className="page-header">
      <div>
        <h1>Я дарю</h1>
        <p className="muted small">Ваши брони. Получатели об этом не знают — ни один из них.</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {шапка}
        <SkeletonRows label="Загружаем ваши брони" />
      </>
    );
  }

  const list = gifts ?? [];

  // Группируем по получателю: так видно всю картину по одному человеку сразу.
  const byRecipient = new Map<string, typeof list>();
  for (const gift of list) {
    const bucket = byRecipient.get(gift.recipient.id);
    if (bucket) bucket.push(gift);
    else byRecipient.set(gift.recipient.id, [gift]);
  }

  return (
    <>
      {шапка}

      {list.length === 0 ? (
        <EmptyState
          icon={<IconGiving />}
          title="Вы пока ничего не забронировали"
          text="Откройте список друга и нажмите «Дарю это» — подарок закрепится за вами, чтобы никто не купил то же самое."
          action={
            <Link to="/feed">
              <Button variant="primary">Посмотреть списки друзей</Button>
            </Link>
          }
        />
      ) : (
        <div className="stack stack--lg">
          <Notice tone="success" icon={<IconEyeOff size={19} />}>
            <span className="small">Всё, что здесь показано, видно только вам.</span>
          </Notice>

          {[...byRecipient.entries()].map(([recipientId, gifts]) => {
            const recipient = gifts[0]!.recipient;
            return (
              <section key={recipientId} className="stack">
                <div className="row">
                  <Avatar name={recipient.displayName} src={recipient.avatarUrl} />
                  <div>
                    <div className="strong">{recipient.displayName}</div>
                    <div className="tiny faint">
                      {pluralize(gifts.length, 'подарок', 'подарка', 'подарков')}
                    </div>
                  </div>
                </div>

                <div className="stack stack--sm">
                  {gifts.map((gift) => (
                    <div key={gift.id} className="card">
                      <div className="row" style={{ padding: 'var(--space-3)' }}>
                        <div
                          style={{
                            width: 56,
                            height: 56,
                            flexShrink: 0,
                            borderRadius: 'var(--radius-sm)',
                            overflow: 'hidden',
                            background: 'var(--bg-input)',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          {gift.item.imageUrl ? (
                            <img
                              src={gift.item.imageUrl}
                              alt=""
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <span aria-hidden="true">🎁</span>
                          )}
                        </div>

                        <div className="grow" style={{ minWidth: 0 }}>
                          <div className="truncate small strong">
                            {gift.item.url ? (
                              <a href={gift.item.url} target="_blank" rel="noreferrer noopener" style={{ color: 'inherit' }}>
                                {gift.item.title}
                              </a>
                            ) : (
                              gift.item.title
                            )}
                          </div>
                          <div className="tiny muted">
                            {gift.item.priceMinor !== null &&
                              formatPrice(gift.item.priceMinor, gift.item.currency as Currency | null)}
                            {gift.item.priceMinor !== null && ' · '}
                            <span>{gift.list.title}</span>
                          </div>
                          <div className="row" style={{ gap: 6, marginTop: 4 }}>
                            {gift.status === 'purchased' ? (
                              <Badge tone="success">Куплено</Badge>
                            ) : (
                              <Badge tone="accent">Забронировано</Badge>
                            )}
                            {gift.anonymous && <Badge>анонимно</Badge>}
                          </div>
                        </div>
                      </div>

                      <div className="row" style={{ padding: '0 var(--space-3) var(--space-3)', gap: 'var(--space-2)' }}>
                        <Button
                          size="sm"
                          variant="ghost"
                          block
                          loading={markPurchased.isPending}
                          onClick={() =>
                            markPurchased.mutate({
                              itemId: gift.item.id,
                              purchased: gift.status !== 'purchased',
                            })
                          }
                        >
                          {gift.status === 'purchased' ? 'Ещё не куплено' : 'Отметить купленным'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="btn--icon"
                          aria-label="Отменить бронь"
                          loading={cancel.isPending}
                          onClick={() => cancel.mutate(gift.item.id)}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
